import { Injectable } from '@angular/core';
import { RiskManagementParameters } from '../models/risk-management-parameters.model';
import { ClustersService } from './clusters.service';
import { ApiRequestsService } from './api-requests.service';
import { Subscription, Subject } from 'rxjs';
import { Cluster } from '../models/cluster.model';
import { MetricsAnalysis } from '../models/analysis/metrics-analysis.model';
import { SimulationAnalysis } from '../models/analysis/simulation-analysis.model';
import { ListsService } from './lists.service';
import { CurrentPortfolio } from '../models/current-portfolio.model';

@Injectable({
  providedIn: 'root'
})
export class RiskManagementService {

  customProtected: CurrentPortfolio;
  customProtectedSubject = new Subject<CurrentPortfolio>();

  currentClusterSubscription: Subscription;

  insuranceParameters: RiskManagementParameters = new RiskManagementParameters();
  simulationParameters: RiskManagementParameters = new RiskManagementParameters();
  saveParameters: RiskManagementParameters = new RiskManagementParameters();

  insuranceMetrics: MetricsAnalysis;
  insuranceMetricsSubject = new Subject<MetricsAnalysis>();
  monteCarloSimulation: SimulationAnalysis;
  monteCarloSimulationSubject = new Subject<SimulationAnalysis>();

  constructor(private apiRequestsService: ApiRequestsService,
              private clustersService: ClustersService,
              private listsService: ListsService) {

    this.currentClusterSubscription = this.clustersService.currentClusterSubject.subscribe(
      (cluster: Cluster) => {
        if (cluster && cluster.has_protected) {
          // Here we should load protected as it already exists
        }
      }
    );
    this.clustersService.emitCurrentCluster();

    // Default risk management parameters
    const parameters = {
      initial_capital: 100,
      aggressiveness: 'moderate',
      rebalancing_frequency: 'monthly',
      target_type: 'maximum_loss',
      protection_level: 10,
      risk_free_rate: 1,
      protection_horizon: ['one_year','three_years'][1],
      simulation_pivot: ['aggressiveness','maximum_loss','maximum_drawdown','protection_horizon'][0],
      metrics_pivot: ['yearly','one_year','three_years'][2]
    };

    this.insuranceParameters.initFromData(parameters);
    this.simulationParameters.initFromData(parameters);
    this.saveParameters.initFromData(parameters);
  }

  emitInsuranceMetrics() {
    // notify subscriptions and provide them with default EFW strategy
    console.log('CALL RiskManagementService.emitInsuranceMetrics()');
    this.insuranceMetricsSubject.next(this.insuranceMetrics);
  }

  emitMonteCarloSimulation() {
    // notify subscriptions and provide them with default EFW strategy
    console.log('CALL RiskManagementService.emitMonteCarloSimulation()');
    this.monteCarloSimulationSubject.next(this.monteCarloSimulation);
  }

  emitCustomProtected() {
    // notify subscriptions and provide them with custom protected
    console.log('CALL RiskManagementService.emitCustomProtected()');
    this.customProtectedSubject.next(this.customProtected);
  }

  async computeInsurance(parameters: RiskManagementParameters, cluster: Cluster) {
    const data: any = await this.apiRequestsService.post('insurance',{
      cluster_id: cluster.id,
      parameters: parameters
    });

    if (data.status=='OK') {
      let metrics_analysis = new MetricsAnalysis();
      metrics_analysis.initFromData(data.data);
      this.insuranceMetrics = metrics_analysis;
      this.emitInsuranceMetrics();
    }
  }

  async computeMonteCarlo(parameters: RiskManagementParameters, cluster: Cluster) {
    const data: any = await this.apiRequestsService.post('monte-carlo',{
      cluster_id: cluster.id,
      parameters: parameters
    });

    if (data.status=='OK') {
      let simulation_analysis = new SimulationAnalysis();
      simulation_analysis.initFromData(data.data);
      this.monteCarloSimulation = simulation_analysis;
      this.emitMonteCarloSimulation();
    }
  }

  async computeProtected(parameters: RiskManagementParameters, cluster: Cluster, do_save: boolean = false) {
    const cluster_id: number = cluster ? cluster.id : undefined;

    const data: any = await this.apiRequestsService.post('compute-protected',{
      cluster_id: cluster.id,
      parameters: parameters,
      do_save: do_save
    });

    if (data.status=='OK') {
      await this.listsService.cleanData(data.data);

      let protected_portfolio = new CurrentPortfolio(cluster_id);
      protected_portfolio.initFromData(data.data);
      this.customProtected = protected_portfolio;
      this.emitCustomProtected();
    }
  }

}
